import { useMemo, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getRoleKey,
  jobReportsKey,
  loadReportsConfigForJob,
  loadReportsConfigForProfile,
  reportsApi,
} from '../api/reports';

export const reportsConfigQueryKeys = {
  all: ['reports-config'],
  byRole: (roleKey) => ['reports-config', roleKey],
};

const pendingCopyCaches = new WeakMap();

/**
 * Copy saves continue on the shared QueryClient after the designer unmounts.
 * The in-flight count has to live there too, or a remount hydrates the
 * pre-invalidation cache and then ignores the save's refetch.
 */
function pendingCopyCache(queryClient) {
  let cache = pendingCopyCaches.get(queryClient);
  if (!cache) {
    const counts = new Map();
    const listeners = new Set();
    const notify = () => listeners.forEach((listener) => listener());
    cache = {
      retain(roleKey) {
        counts.set(roleKey, (counts.get(roleKey) || 0) + 1);
        notify();
      },
      release(roleKey) {
        const next = (counts.get(roleKey) || 0) - 1;
        if (next > 0) counts.set(roleKey, next);
        else counts.delete(roleKey);
        notify();
      },
      count(roleKey) {
        if (!roleKey) return 0;
        return counts.get(roleKey) || 0;
      },
      subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
    };
    pendingCopyCaches.set(queryClient, cache);
  }
  return cache;
}

export function retainReportsCopyTarget(queryClient, roleKey) {
  pendingCopyCache(queryClient).retain(roleKey);
}

export function releaseReportsCopyTarget(queryClient, roleKey) {
  pendingCopyCache(queryClient).release(roleKey);
}

export function useReportsCopyPending(queryClient, roleKey) {
  const cache = pendingCopyCache(queryClient);
  return useSyncExternalStore(
    cache.subscribe,
    () => cache.count(roleKey),
    () => 0,
  );
}

/**
 * Shared React Query cache for role report configuration.
 * Keyed by role so ReportsPage, DashboardPage, and ReportDesignerPage reuse warm data.
 */
export function useReportsConfig({ profile, jobId, levelType, enabled = true } = {}) {
  const roleKey = useMemo(() => {
    if (profile) return getRoleKey(profile);
    if (jobId != null && jobId !== '') return jobReportsKey(jobId);
    return null;
  }, [profile, jobId]);

  return useQuery({
    queryKey: reportsConfigQueryKeys.byRole(roleKey),
    queryFn: () => {
      if (profile) return loadReportsConfigForProfile(profile);
      return loadReportsConfigForJob(jobId, levelType ?? jobId);
    },
    enabled: enabled && Boolean(roleKey),
  });
}

/** Save report config and invalidate the cached entry for that role. */
export function useSaveReportsConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleKey, config }) => reportsApi.saveConfig(roleKey, config),
    onSuccess: (_data, { roleKey }) => {
      queryClient.invalidateQueries({ queryKey: reportsConfigQueryKeys.byRole(roleKey) });
    },
  });
}
